import { Navigate, Route, Routes } from 'react-router-dom';

import { customerRoutes } from 'noddi-util';
import { useReleaseStationaryHoldOnClear } from 'noddi-web';
import { useCallback } from 'react';

import { MaintenanceContent } from '@components/Common/MaintenanceScreen';
import { Error404PageWithTranslations } from '@components/Error/Error404PageWithTranslations';
import { useStationaryBookingStore } from '../stores/StationaryBookingStore';
import { getNoddiRemoteConfig } from '../utils/remoteConfigSetup';
import { StationaryBookingCar } from './BookingFlow/Stationary/Car';
import { StationaryBookingConfirmationNoPayment } from './BookingFlow/Stationary/Confirmation/StationaryBookingConfirmationNoPayment';
import { StationaryBookingConfirmationWithPayment } from './BookingFlow/Stationary/Confirmation/StationaryBookingConfirmationWithPayment';
import { StationaryBookingLogin } from './BookingFlow/Stationary/Login';
import { PayForStationaryBooking } from './BookingFlow/Stationary/PayForBooking';
import { StationaryBookingServices } from './BookingFlow/Stationary/Services';
import { StationaryBookingSummary } from './BookingFlow/Stationary/Summary';
import { StationaryBookingTimeWindowPicker } from './BookingFlow/Stationary/TimeWindows';
import { StationaryBookingTireSeason } from './BookingFlow/Stationary/TireSeason';

export function StationaryBookingRouter() {
  const subscribeHold = useCallback(
    (
      listener: (
        current: ReturnType<typeof useStationaryBookingStore.getState>['reservation'],
        previous: ReturnType<typeof useStationaryBookingStore.getState>['reservation']
      ) => void
    ) =>
      useStationaryBookingStore.subscribe((state, prevState) => {
        listener(state.reservation, prevState.reservation);
      }),
    []
  );
  useReleaseStationaryHoldOnClear(subscribeHold);

  const maintenanceScreenBooking = getNoddiRemoteConfig('maintenanceScreenBooking');

  if (maintenanceScreenBooking?.shouldShow) {
    return <MaintenanceContent maintenanceScreen={maintenanceScreenBooking} />;
  }

  // Public Trønderdekk entry used to be /bookings/service-center/choice — keep it working.
  const legacyChoiceRedirect = <Navigate to={customerRoutes.newBookingChoice.getPath()} replace />;

  return (
    <Routes>
      <Route index element={legacyChoiceRedirect} />
      {/* oxlint-disable-next-line typescript/no-deprecated -- legacy URL kept as a redirect source */}
      <Route path={customerRoutes.newStationaryBookingChoice.getBasePath()} element={legacyChoiceRedirect} />
      <Route path={customerRoutes.newStationaryBookingCar.getBasePath()} element={<StationaryBookingCar />} />
      <Route path={customerRoutes.newStationaryBookingServices.getBasePath()} element={<StationaryBookingServices />} />
      <Route
        path={customerRoutes.newStationaryBookingTireSeason.getBasePath()}
        element={<StationaryBookingTireSeason />}
      />
      <Route
        path={customerRoutes.newStationaryBookingTimeWindow.getBasePath()}
        element={<StationaryBookingTimeWindowPicker />}
      />
      <Route path={customerRoutes.newStationaryBookingLogin.getBasePath()} element={<StationaryBookingLogin />} />
      <Route path={customerRoutes.newStationaryBookingSummary.getBasePath()} element={<StationaryBookingSummary />} />
      <Route
        path={customerRoutes.newStationaryBookingPayForBooking.getBasePath()}
        element={<PayForStationaryBooking />}
      />
      <Route
        path={customerRoutes.newStationaryBookingConfirmation.getBasePath()}
        element={<StationaryBookingConfirmationWithPayment />}
      />
      <Route
        path={customerRoutes.newStationaryBookingConfirmationNoPayment.getBasePath()}
        element={<StationaryBookingConfirmationNoPayment />}
      />
      <Route path='*' element={<Error404PageWithTranslations />} />
    </Routes>
  );
}
